import { useState } from 'react'
import { Box, Collapse, Grid, IconButton, Typography } from '@mui/material'
import MailOutline from '@mui/icons-material/MailOutlineOutlined'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'

import { messageDateInfo } from '../Chat/utils'
import { useManageSelectedTodo } from '../useManageSelectedTodo'
import { EmailListView } from './EmailListView'
import { RichTextEditorDisplay } from '../RichTextEditor/RichTextEditorDisplay'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'
import { required } from '~/shared/lib/required'

type EmailCommunication = Extract<
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object'],
  { kind: 'email' }
>

const EmailChatMessage = (props: {
  email: Pick<
    EmailCommunication,
    'id' | 'created_at' | 'from_address' | 'to_address' | 'snippet'
  > & {
    case_relations: {
      case: Pick<EmailCommunication['case_relations'][number]['case'], 'id' | 'description'>
    }[]
  }
  highlightChatMessageContent?: boolean
}) => {
  const { email } = props
  const [showEmailModel, setShowEmailModel] = useState<boolean>(false)
  const emailCase = required(email.case_relations[0], 'a case relation on the email').case
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const { navigateToCase } = useManageSelectedTodo()

  const { messageDate } = messageDateInfo(email.created_at)

  return (
    <Box
      sx={{
        mt: 2,
        marginLeft: '10%',
        ...(props.highlightChatMessageContent && {
          backgroundColor: 'background.highlight',
          borderRadius: 1,
          p: 1,
        }),
      }}
    >
      <Grid direction="row" container sx={{ alignItems: 'center' }}>
        <Grid
          container
          size={12}
          direction="row"
          title={messageDate}
          data-id={`email-${email.id}`}
          sx={{ alignItems: 'center' }}
        >
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <MailOutline fontSize="small" sx={{ color: '#878D96' }} />
          </Grid>
          <Grid sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
            <Typography sx={{ color: '#878D96', fontSize: 12 }}>#{emailCase.id}</Typography>
          </Grid>
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              edge="start"
              onClick={() => setIsExpanded(c => !c)}
              sx={{ paddingTop: '-10px' }}
            >
              {isExpanded ? (
                <KeyboardArrowUp fontSize="small" color="secondary" />
              ) : (
                <KeyboardArrowDown fontSize="small" color="secondary" />
              )}
            </IconButton>
          </Grid>
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                background: 'linear-gradient(90deg,rgba(0,0,0,0.25), rgba(0,0,0,0))',
                bottom: 0,
                height: '1px',
                width: '100px',
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Collapse in={isExpanded}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'grey.50',
            mt: 1,
            padding: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', lineHeight: 1.5 }}>
            <Typography
              component="span"
              variant="caption"
              sx={{ wordBreak: 'break-word', whiteSpace: 'normal', mr: 1 }}
            >
              {email.from_address}&nbsp;&nbsp; to&nbsp;&nbsp;
              {email.to_address}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="caption">{messageDate}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {emailCase ? <Typography variant="caption">{emailCase.description}</Typography> : null}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {email.snippet ? (
              <Typography
                component="span"
                variant="caption"
                sx={{ wordBreak: 'break-word', whiteSpace: 'normal', mr: 1 }}
              >
                <RichTextEditorDisplay content={email.snippet} />
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {emailCase ? (
              <Typography
                variant="caption"
                sx={{
                  textDecoration: 'underline',
                  color: 'primary.600',
                }}
                onClick={() => navigateToCase(emailCase.id)}
              >{`Case ID #${emailCase.id}`}</Typography>
            ) : null}
            <Typography variant="caption">&nbsp;&nbsp;|&nbsp;&nbsp;</Typography>

            <Typography
              variant="caption"
              sx={{
                textDecoration: 'underline',
                color: 'primary.600',
              }}
              onClick={() => {
                navigateToCase(emailCase.id)
                setShowEmailModel(true)
              }}
            >
              View Email
            </Typography>
          </Box>
          {showEmailModel && (
            <EmailListView selectedCaseId={emailCase.id} onClose={() => setShowEmailModel(false)} />
          )}
        </Box>
      </Collapse>
      {showEmailModel && (
        <EmailListView selectedCaseId={emailCase.id} onClose={() => setShowEmailModel(false)} />
      )}
    </Box>
  )
}

export default EmailChatMessage
